import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client/react'
import { gql } from '@apollo/client'
import {
  Grid,
  Card,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Chip,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Switch,
  Divider,
  FormControlLabel,
} from '@mui/material'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSnackbar } from 'notistack'
import { PageTitle } from '@/layout-components'
import { ExampleWrapperSeamless } from '@/layout-components'
import withSession from '@/housefolios-components/Session/withSession'
import AddContactDialog from '../../housefolios-components/Dialogs/AddContact'
import UploadContactsDialog from '../../housefolios-components/Contacts/UploadContacts'
import ContactsTable from './ContactsTable'
import { IMAGE_UPLOAD } from '@/housefolios-components/TitleButtons/AddPropertyButton/mutations'
// Centralized lodash imports - much cleaner!
import { isEmpty, map, compact, uniq } from '@/utils/lodash'
import HardMoneyLenderQuestions from './HardMoneyLenderQuestions'

const GET_CONTACTS = gql`
  query GetContacts {
    contacts {
      _id
      name
      types
      email
      phone
      company
      notes
      address
      city
      state
      area
      ranking
      image {
        _id
        url
        url_small
      }
      optionalFields
      createdAt
      updatedAt
    }
  }
`

const DELETE_CONTACT = gql`
  mutation DeleteContact($_id: ID!) {
    deleteContact(_id: $_id)
  }
`

const UPDATE_CONTACT = gql`
  mutation UpdateContact($_id: ID!, $input: ContactInput!) {
    updateContact(_id: $_id, input: $input) {
      _id
      name
      types
      email
      phone
      company
      notes
      address
      city
      state
      area
      ranking
      image {
        _id
        url
        url_small
      }
      optionalFields
      createdAt
      updatedAt
    }
  }
`

const CREATE_CONTACT = gql`
  mutation CreateContact($input: ContactInput!) {
    createContact(input: $input) {
      _id
      name
      types
      email
      phone
      company
      notes
      address
      city
      state
      area
      ranking
      image {
        _id
        url
        url_small
      }
      optionalFields
    }
  }
`

// Contact type configuration with colors and icons
const CONTACT_TYPES_CONFIG = {
  Agent: {
    color: '#3B82F6',
    backgroundColor: '#DBEAFE',
    icon: 'user',
    style: 'far',
    label: 'Agent',
  },
  Buyer: {
    color: '#10B981',
    backgroundColor: '#DBEAFE',
    icon: 'user',
    style: 'far',
    label: 'Buyer',
  },
  Seller: {
    color: '#F59E0B',
    backgroundColor: '#DBEAFE',
    icon: 'user',
    style: 'far',
    label: 'Seller',
  },
  Investor: {
    color: '#8B5CF6',
    backgroundColor: '#DBEAFE',
    icon: 'building',
    style: 'far',
    label: 'Investor',
  },
  Contractor: {
    color: '#EF4444',
    backgroundColor: '#DBEAFE',
    icon: 'hammer',
    style: 'fas',
    label: 'Contractor',
  },
  'Property Manager': {
    color: '#06B6D4',
    backgroundColor: '#DBEAFE',
    icon: 'home',
    style: 'fas',
    label: 'Property Manager',
  },
  'Hard Money Lender': {
    color: '#84CC16',
    backgroundColor: '#DBEAFE',
    icon: 'dollar-sign',
    style: 'fas',
    label: 'Hard Money Lender',
  },
  'Insurance Agent': {
    color: '#F97316',
    backgroundColor: '#DBEAFE',
    icon: 'file-alt',
    style: 'far',
    label: 'Insurance Agent',
  },
  'Title Company': {
    color: '#EC4899',
    backgroundColor: '#DBEAFE',
    icon: 'file-alt',
    style: 'far',
    label: 'Title Company',
  },
  Other: {
    color: '#6B7280',
    backgroundColor: '#DBEAFE',
    icon: 'ellipsis-h',
    style: 'fas',
    label: 'Other',
  },
}

function Contacts() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedRank, setSelectedRank] = useState([])
  const [selectedState, setSelectedState] = useState('all')
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [questionContact, setQuestionContract] = useState(null)
  const [isCategoryPrefsOpen, setIsCategoryPrefsOpen] =
    useState(false)
  const [categoryOrder, setCategoryOrder] = useState([])
  const [hiddenCategories, setHiddenCategories] = useState(new Set())
  const { enqueueSnackbar } = useSnackbar()

  const { loading, error, data, refetch } = useQuery(GET_CONTACTS)
  const [deleteContact] = useMutation(DELETE_CONTACT)
  const [updateContact] = useMutation(UPDATE_CONTACT)
  const [createContact] = useMutation(CREATE_CONTACT)

  const handleSearch = (event) => {
    setSearchTerm(event.target.value)
  }

  const handleTypeSelect = (type) => {
    setSelectedType(type)
  }
  const handleRankFilter = (event, newValue) => {
    setSelectedRank(newValue)
  }
  const handleStateFilter = (event) => {
    setSelectedState(event.target.value)
  }

  // Convert current filter values to contact defaults
  const getContactDefaults = () => {
    const defaults = {}

    // Convert selected type to contact types array
    if (selectedType !== 'all') {
      defaults.types = [selectedType]
    }

    // Convert selected ranks to single ranking (use first selected)
    if (selectedRank.length > 0) {
      defaults.ranking = selectedRank[0].value
    }

    // Convert selected state
    if (selectedState !== 'all') {
      defaults.state = selectedState
    }

    return defaults
  }

  const [imageUpload] = useMutation(IMAGE_UPLOAD)

  // Initialize category order and hidden categories from localStorage
  useEffect(() => {
    const allTypes = Object.keys(CONTACT_TYPES_CONFIG)
    try {
      const stored = JSON.parse(
        localStorage.getItem('contactsCategoryPrefs') || 'null',
      )
      if (stored && Array.isArray(stored.order)) {
        // Ensure stored order contains all types; append any new ones
        const missing = allTypes.filter(
          (t) => !stored.order.includes(t),
        )
        setCategoryOrder([...stored.order, ...missing])
      } else {
        setCategoryOrder(allTypes)
      }
      if (stored && Array.isArray(stored.hidden)) {
        setHiddenCategories(new Set(stored.hidden))
      }
    } catch (e) {
      setCategoryOrder(allTypes)
      setHiddenCategories(new Set())
    }
  }, [])

  const persistCategoryPrefs = (order, hiddenSet) => {
    try {
      localStorage.setItem(
        'contactsCategoryPrefs',
        JSON.stringify({ order, hidden: Array.from(hiddenSet)  }),
      )
    } catch (e) {
      // ignore storage errors
    }
  }

  const moveCategory = (type, direction) => {
    setCategoryOrder((prev) => {
      const index = prev.indexOf(type)
      if (index === -1) return prev
      const newOrder = [...prev]
      const swapWith = direction === 'up' ? index - 1 : index + 1
      if (swapWith < 0 || swapWith >= newOrder.length) return prev
      const temp = newOrder[swapWith]
      newOrder[swapWith] = newOrder[index]
      newOrder[index] = temp
      const newHidden = new Set(hiddenCategories)
      persistCategoryPrefs(newOrder, newHidden)
      return newOrder
    })
  }

  const toggleHiddenCategory = (type, shouldShow) => {
    setHiddenCategories((prev) => {
      const next = new Set(prev)
      if (shouldShow) next.delete(type)
      else next.add(type)
      if (selectedType !== 'all' && next.has(selectedType)) {
        setSelectedType('all')
      }
      persistCategoryPrefs(categoryOrder, next)
      return next
    })
  }

  const handleAddContact = async (contactData, files = []) => {
    try {
      await createContact({
        variables: {
          input: contactData,
        },
      }).then(({ data  }) => {
        if (!isEmpty(files)) {
          imageUpload({
            variables: {
              documentId: data?.createContact?._id,
              imageUrls: files,
              collection: 'contact',
            },
          })
        }
      })
      enqueueSnackbar('Contact created successfully', {
        variant: 'success',
      })
      refetch()
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error'  })
    }
  }

  const handleEditContact = async (contactData, files = []) => {
    try {
      await updateContact({
        variables: {
          _id: editingContact._id,
          input: contactData,
        },
      }).then(({ data  }) => {
        if (!isEmpty(files)) {
          imageUpload({
            variables: {
              documentId: editingContact._id,
              imageUrls: files,
              collection: 'contact',
            },
          })
        }
      })
      enqueueSnackbar('Contact updated successfully', {
        variant: 'success',
      })
      setEditingContact(null)
      refetch()
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error'  })
    }
  }

  const handleEditContactQuestions = async (contactData) => {
    try {
      await updateContact({
        variables: {
          _id: questionContact._id,
          input: contactData,
        },
      })
      enqueueSnackbar('Contact updated successfully', {
        variant: 'success',
      })
      setQuestionContract(null)
      refetch()
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error'  })
    }
  }

  const handleDeleteContact = async (_id) => {
    try {
      await deleteContact({
        variables: { _id },
      })
      enqueueSnackbar('Contact deleted successfully', {
        variant: 'success',
      })
      refetch()
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error'  })
    }
  }

  const filteredContacts =
    data?.contacts?.filter((contact) => {
      const matchesSearch =
        contact.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        contact.email
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        contact.company
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        contact.phone
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        contact.address
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        contact.city
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        contact.notes
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())

      const matchesType =
        selectedType === 'all' || contact.types.includes(selectedType)

      const matchesRank =
        selectedRank.length === 0 ||
        selectedRank.some((rank) => rank.value === contact.ranking)

      const matchesState =
        selectedState === 'all' || contact.state === selectedState

      return (
        matchesSearch && matchesType && matchesRank && matchesState
      )
    }) || []

  // Calculate statistics
  const totalContacts = data?.contacts?.length || 0
  const highRatedContacts =
    data?.contacts?.filter((contact) => contact.ranking >= 4)
      .length || 0
  const missingInfoContacts =
    data?.contacts?.filter(
      (contact) =>
        !contact.email || !contact.phone || !contact.ranking,
    ).length || 0
  // const activeContacts =
  //   data?.contacts?.filter((contact) => contact.ranking >= 4)
  //     .length || 0

  // Get contact type counts
  const getContactTypeCount = (type) => {
    return (
      data?.contacts?.filter((contact) =>
        contact.types.includes(type),
      ).length || 0
    )
  }

  if (error) {
    enqueueSnackbar('Error loading contacts: ' + error.message, {
      variant: 'error',
    })
  }

  return (
    <>
      <PageTitle
        titleHeading="Contacts"
        titleDescription="Manage your network of real estate professionals"
        icon={
          <FontAwesomeIcon
            icon={['far', 'address-book']}
            style={{ fontSize: 30 }}

            sx={{ color: '#368cff' }}
          />
        }
      />

      <ExampleWrapperSeamless>
        {/* Contact Type Section */}
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: '#374151', fontWeight: 600 }}
              >
                Contact Type
              </Typography>
              <Button
                size="small"
                variant="text"
                startIcon={
                  <FontAwesomeIcon icon={['fas', 'sliders-h']} />
                }
                onClick={() => setIsCategoryPrefsOpen(true)}
              >
                Customize
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {/* All option */}
              <Chip
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={['fas', 'user-friends']}
                      style={{ fontSize: '14px' }}
                    />
                    <span>All</span>
                    <Box
                      sx={{
                        backgroundColor:
                          selectedType === 'all'
                            ? 'white'
                            : '#1f2937',
                        color:
                          selectedType === 'all'
                            ? '#1f2937'
                            : 'white',
                        borderRadius: '12px',
                        px: 1,
                        py: 0.25,
                        fontSize: '12px',
                        fontWeight: 600,
                        minWidth: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {totalContacts}
                    </Box>
                  </Box>
                }
                onClick={() => handleTypeSelect('all')}
                sx={{
                  backgroundColor:
                    selectedType === 'all'
                      ? 'black'
                      : '#38BDF8 !important',
                  color: selectedType === 'all' ? 'white' : 'black',
                  border: `2px solid ${selectedType === 'all' ? 'black' : 'transparent'}`,
                  fontWeight: 600,
                  fontSize: '14px',
                  height: '40px',
                  '&:hover': {
                    backgroundColor:
                      selectedType === 'all'
                        ? 'black'
                        : '#38BDF8 !important',
                    opacity: 0.8,
                  },
                  '& .MuiChip-label': {
                    px: 2,
                  },
                }}
              />

              {/* Contact types in alphabetical order */}
              {(categoryOrder.length
                ? categoryOrder
                : Object.keys(CONTACT_TYPES_CONFIG)
              )
                .filter((type) => !hiddenCategories.has(type))
                .map((type) => {
                  const config = CONTACT_TYPES_CONFIG[type]
                  const count = getContactTypeCount(type)
                  const isSelected = selectedType === type

                  return (
                    <Chip
                      key={type}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <FontAwesomeIcon
                            icon={[
                              config.style || 'far',
                              config.icon,
                            ]}
                            style={{ fontSize: '14px' }}
                          />
                          <span>{config.label}</span>
                          <Box
                            sx={{
                              backgroundColor: isSelected
                                ? 'white'
                                : config.color,
                              color: isSelected
                                ? config.color
                                : 'white',
                              borderRadius: '12px',
                              px: 1,
                              py: 0.25,
                              fontSize: '12px',
                              fontWeight: 600,
                              minWidth: '20px',
                              textAlign: 'center',
                            }}
                          >
                            {count}
                          </Box>
                        </Box>
                      }
                      onClick={() => handleTypeSelect(type)}
                      sx={{
                        backgroundColor: isSelected
                          ? config.color
                          : '#38BDF8 !important',
                        color: isSelected ? 'white' : config.color,
                        border: `2px solid ${isSelected ? config.color : 'transparent'}`,
                        fontWeight: 600,
                        fontSize: '14px',
                        height: '40px',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? config.color
                            : '#38BDF8 !important',
                          opacity: 0.8,
                        },
                        '& .MuiChip-label': {
                          px: 2,
                        },
                      }}
                    />
                  )
                })}
            </Box>
          </Box>
        </Card>

        {/* Key Indicators Section */}
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: '#374151', fontWeight: 600 }}
            >
              Key Indicators
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: '#F3F4F6',
                    borderRadius: '12px',
                    p: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#111827',
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    {totalContacts}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#6B7280', fontWeight: 500 }}
                  >
                    Total Contacts
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: '#D1FAE5',
                    borderRadius: '12px',
                    p: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#065F46',
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    {highRatedContacts}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#065F46', fontWeight: 500 }}
                  >
                    High Rated (4+ Stars)
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: '#FEE2E2',
                    borderRadius: '12px',
                    p: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#DC2626',
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    {missingInfoContacts}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#DC2626', fontWeight: 500 }}
                  >
                    Missing Info
                  </Typography>
                </Paper>
              </Grid>
              {/* <Grid size={{xs: 12, sm: 6, md: 3}}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: '#D1FAE5',
                    borderRadius: '12px',
                    p: 2,
                    textAlign: 'center'}}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#065F46',
                      fontWeight: 700,
                      mb: 0.5}}
                  >
                    {activeContacts}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#065F46', fontWeight: 500 }}
                  >
                    Active Contacts
                  </Typography>
                </Paper>
              </Grid> */}
            </Grid>
          </Box>
        </Card>

        {/* Search and Filters */}
        <Card
          sx={{
            borderRadius: '16px',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
          }}
        >
          <Box sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4,
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  flex: 1,
                  flexWrap: { xs: 'wrap', lg: 'nowrap' },
                  alignItems: 'center',
                }}
              >
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={handleSearch}
                  sx={{
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: '#f9fafb',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        boxShadow:
                          '0 0 0 2px rgba(59, 130, 246, 0.2)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#6b7280',
                      fontWeight: 500,
                    },
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <FontAwesomeIcon
                          icon={['fas', 'search']}
                          style={{
                            marginRight: 8,
                            color: '#9ca3af',
                            fontSize: '16px',
                          }}
                        />
                      ),
                    },
                  }}
                />

                <Autocomplete
                  multiple
                  options={[
                    { value: 5, label: '5 - Excellent' },
                    { value: 4, label: '4 - Good' },
                    { value: 3, label: '3 - Average' },
                    { value: 2, label: '2 - Poor' },
                    { value: 1, label: '1 - Very Poor' },
                  ]}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  value={selectedRank}
                  onChange={handleRankFilter}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Rank"
                      variant="outlined"
                      size="small"
                      sx={{
                        minWidth: { xs: '100%', sm: 200 },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#f9fafb',
                          '&:hover': {
                            backgroundColor: '#f3f4f6',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'white',
                            boxShadow:
                              '0 0 0 2px rgba(59, 130, 246, 0.2)',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#6b7280',
                          fontWeight: 500,
                        },
                      }}
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getItemProps({ index })}
                        key={option.value}
                        label={option.label}
                        size="small"
                        sx={{
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          borderRadius: '8px',
                          fontWeight: 500,
                          '& .MuiChip-deleteIcon': {
                            color: 'white',
                            '&:hover': {
                              color: '#ef4444',
                            },
                          },
                        }}
                      />
                    ))
                  }
                />
                <TextField
                  select
                  label="State"
                  variant="outlined"
                  size="small"
                  value={selectedState}
                  onChange={handleStateFilter}
                  sx={{
                    minWidth: { xs: '100%', sm: 150 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: '#f9fafb',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        boxShadow:
                          '0 0 0 2px rgba(59, 130, 246, 0.2)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#6b7280',
                      fontWeight: 500,
                    },
                  }}
                >
                  <MenuItem value="all">All States</MenuItem>
                  {map(
                    uniq(
                      compact(
                        map(
                          data?.contacts,
                          (contact) => contact?.state,
                        ),
                      ),
                    ),
                    (state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ),
                  )}
                </TextField>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  <FontAwesomeIcon icon={['fas', 'user-plus']} />
                }
                onClick={() => setIsAddDialogOpen(true)}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    boxShadow:
                      '0 6px 8px -1px rgba(59, 130, 246, 0.4)',
                  },
                }}
              >
                Add Contact
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={
                  <FontAwesomeIcon icon={['far', 'file-excel']} />
                }
                onClick={() => setIsUploadDialogOpen(true)}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  '&:hover': {
                    borderColor: '#2563eb',
                    backgroundColor: '#f8fafc',
                  },
                }}
              >
                Upload Contacts
              </Button>
            </Box>

            <ContactsTable
              contacts={filteredContacts}
              onEdit={setEditingContact}
              onDelete={handleDeleteContact}
              onQuestion={setQuestionContract}
              loading={loading}
            />
          </Box>
        </Card>
      </ExampleWrapperSeamless>

      <AddContactDialog
        open={isAddDialogOpen || !!editingContact}
        onClose={() => {
          setIsAddDialogOpen(false)
          setEditingContact(null)
        }}
        onSave={editingContact ? handleEditContact : handleAddContact}
        contact={editingContact}
        defaultValues={isAddDialogOpen ? getContactDefaults() : null}
      />

      <UploadContactsDialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onContactsImported={refetch}
      />

      {/* Category Preferences Dialog */}
      <Dialog
        open={isCategoryPrefsOpen}
        onClose={() => setIsCategoryPrefsOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Customize Categories</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Show/hide and reorder categories.
          </Typography>
          <List dense>
            {(categoryOrder.length
              ? categoryOrder
              : Object.keys(CONTACT_TYPES_CONFIG)
            ).map((type) => (
              <ListItem key={type} divider>
                <IconButton
                  size="small"
                  onClick={() => moveCategory(type, 'up')}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moveCategory(type, 'down')}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
                <Box sx={{ ml: 1, mr: 2 }}>
                  <FontAwesomeIcon
                    icon={[
                      CONTACT_TYPES_CONFIG[type].style || 'far',
                      CONTACT_TYPES_CONFIG[type].icon,
                    ]}
                    style={{
                      color: CONTACT_TYPES_CONFIG[type].color,
                    }}
                  />
                </Box>
                <ListItemText primary={type} />
                {(() => {
                  const isShown = !hiddenCategories.has(type)
                  return (
                    <FormControlLabel
                      label={isShown ? 'Show' : 'Hide'}
                      labelPlacement="end"
                      control={
                        <Switch
                          checked={isShown}
                          onChange={(e, checked) =>
                            toggleHiddenCategory(type, checked)
                          }
                          color="primary"
                          size="medium"
                        />
                      }
                      sx={{
                        ml: 2,
                        mr: 0,
                        alignItems: 'center',
                        '& .MuiFormControlLabel-label': {
                          ml: 2,
                          minWidth: 48,
                          fontWeight: 500,
                          color: '#374151',
                        },
                      }}
                    />
                  )
                })()}
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Button
              size="small"
              onClick={() => {
                const allTypes = Object.keys(CONTACT_TYPES_CONFIG)
                setCategoryOrder(allTypes)
                const empty = new Set()
                setHiddenCategories(empty)
                persistCategoryPrefs(allTypes, empty)
              }}
            >
              Reset
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCategoryPrefsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <HardMoneyLenderQuestions
        open={!!questionContact}
        onClose={() => {
          // setIsAddDialogOpen(false)
          setQuestionContract(null)
        }}
        onSave={handleEditContactQuestions}
        contact={questionContact}
      />
    </>
  )
}

export default withSession(Contacts)
